import React from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, Shield, Ban, AlertTriangle, TrendingDown, Scale, FileText, Clock } from 'lucide-react';

const FCAActionOnGAPPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="py-20 lg:py-28 bg-primary">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/30 mb-6">
                <Shield className="w-4 h-4 text-gold" />
                <span className="text-sm font-medium text-gold">Blue Lion Law</span>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-primary-foreground leading-tight mb-6">
                FCA Action on GAP Insurance: 80% of Providers Suspend Sales
              </h1>
              <p className="text-lg text-primary-foreground/80 leading-relaxed">
                The regulator has finally intervened — and the findings are damning
              </p>
            </div>
          </div>
        </section>

        {/* Intro Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <p className="text-xl text-foreground leading-relaxed">
                The Financial Conduct Authority (FCA) has taken unprecedented action against the GAP 
                insurance industry. Following a review that exposed shocking practices, <strong>80% of 
                GAP insurance providers have suspended sales</strong>. The regulator has made clear: 
                these products do not offer "fair value" to consumers.
              </p>
            </div>
          </div>
        </section>

        {/* Damning Statistics Section */}
        <section className="py-16 lg:py-20 bg-destructive/5">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                  <TrendingDown className="w-6 h-6 text-destructive" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  The Damning Statistics
                </h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-8">
                The FCA's Value Measures Data report laid bare the reality of GAP insurance sales:
              </p>

              <div className="grid md:grid-cols-3 gap-6">
                <Card className="text-center border-destructive/50">
                  <CardContent className="p-8">
                    <div className="text-5xl font-bold text-destructive mb-3">6%</div>
                    <p className="text-foreground font-medium mb-2">Paid Out in Claims</p>
                    <p className="text-sm text-muted-foreground">
                      For every £100 paid in, just £6 was paid out.
                    </p>
                  </CardContent>
                </Card>
                <Card className="text-center border-gold/50">
                  <CardContent className="p-8">
                    <div className="text-5xl font-bold text-gold mb-3">70%</div>
                    <p className="text-foreground font-medium mb-2">Commission to Dealers</p>
                    <p className="text-sm text-muted-foreground">
                      The majority of your money never went towards cover.
                    </p>
                  </CardContent>
                </Card>
                <Card className="text-center border-accent/50">
                  <CardContent className="p-8">
                    <div className="text-5xl font-bold text-accent mb-3">80%</div>
                    <p className="text-foreground font-medium mb-2">Sales Suspended</p>
                    <p className="text-sm text-muted-foreground">
                      Providers couldn't prove fair value.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <Card className="mt-8 bg-muted/50">
                <CardContent className="p-6">
                  <p className="text-foreground text-center">
                    These figures are staggering. They reveal an industry that was <strong>systematically extracting 
                    money from consumers</strong> while providing minimal actual value.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* FCA Position Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                  <Scale className="w-6 h-6 text-accent" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  The FCA's Position
                </h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-6">
                Sheldon Mills, the FCA's Executive Director of Consumers and Competition, has been 
                unequivocal:
              </p>

              <Card className="bg-primary/5 border-primary">
                <CardContent className="p-8">
                  <blockquote className="text-xl text-foreground italic text-center">
                    "While GAP insurance can be a useful product, we have seen that too often it 
                    <span className="text-accent font-semibold"> does not provide fair value</span>. 
                    We expect to see improvements that ensure products genuinely meet customer needs."
                  </blockquote>
                  <p className="text-center text-muted-foreground mt-4">
                    — Sheldon Mills, FCA Executive Director
                  </p>
                </CardContent>
              </Card>

              <p className="text-lg text-muted-foreground mt-8">
                The FCA has warned insurers that they must prove their products offer fair value under 
                the new Consumer Duty rules. Until they can demonstrate this, sales have effectively 
                been halted across most of the market.
              </p>
            </div>
          </div>
        </section>

        {/* What This Means Section */}
        <section className="py-16 lg:py-20 bg-muted/30">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-gold" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  What This Means for Past Customers
                </h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-6">
                If 80% of providers can't currently prove their products offer fair value, <strong className="text-foreground">what does 
                that say about the products they've been selling for years?</strong>
              </p>

              <Card className="bg-gold/5 border-gold mb-8">
                <CardContent className="p-6">
                  <p className="text-foreground">
                    The FCA's intervention effectively validates what many suspected: <strong>GAP insurance 
                    has been systematically mis-sold to consumers</strong> who were never told about the 
                    enormous commissions being paid to dealers.
                  </p>
                </CardContent>
              </Card>

              <p className="text-lg text-muted-foreground">
                If you purchased GAP insurance before this intervention, you may have grounds for a 
                claim — not because the product itself was bad, but because of <strong className="text-foreground">how it was sold and the 
                information that was withheld from you</strong>.
              </p>
            </div>
          </div>
        </section>

        {/* Consumer Duty Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-accent" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  The Consumer Duty Factor
                </h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-6">
                The FCA's new Consumer Duty rules require firms to:
              </p>

              <div className="grid md:grid-cols-2 gap-4 mb-8">
                <Card className="border-l-4 border-l-accent">
                  <CardContent className="p-4">
                    <p className="text-foreground">Act in good faith towards retail customers</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-gold">
                  <CardContent className="p-4">
                    <p className="text-foreground">Avoid causing foreseeable harm</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-accent">
                  <CardContent className="p-4">
                    <p className="text-foreground">Enable customers to pursue their financial objectives</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-gold">
                  <CardContent className="p-4">
                    <p className="text-foreground">Provide products that offer fair value</p>
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-destructive/5 border-destructive">
                <CardContent className="p-6">
                  <p className="text-foreground">
                    It's now clear that GAP insurance sales practices <strong>failed on multiple counts</strong>. The 
                    commission structures, the failure to disclose alternatives, and the pressure selling 
                    at point of sale all contributed to an environment where consumers were not treated fairly.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Different from PPI */}
        <section className="py-16 lg:py-20 bg-primary">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <Clock className="w-8 h-8 text-gold" />
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary-foreground">
                  Why This Is Different from PPI
                </h2>
              </div>
              
              <p className="text-lg text-primary-foreground/80 mb-6">
                Unlike PPI, where the scandal took years to fully emerge, the GAP insurance issue has 
                been identified and acted upon more quickly. The FCA has learned from past mistakes 
                and is taking proactive steps.
              </p>

              <Card className="bg-primary-foreground/10 border-none mb-6">
                <CardContent className="p-6">
                  <p className="text-primary-foreground text-center">
                    However, this also means that <strong className="text-gold">there is no formal complaints deadline yet</strong>. 
                    The window for claims is open, but it won't stay open forever.
                  </p>
                </CardContent>
              </Card>

              <p className="text-lg text-primary-foreground/90">
                When PPI deadlines were finally set, <strong className="text-primary-foreground">millions of valid claims were never made</strong> because 
                people didn't act in time.
              </p>
            </div>
          </div>
        </section>

        {/* Industry Response */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                  <Ban className="w-6 h-6 text-accent" />
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
                  The Industry Response
                </h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-6">
                The fact that <strong className="text-foreground">80% of providers have voluntarily suspended sales</strong> speaks volumes. These 
                companies have effectively admitted that their current products and practices cannot 
                meet the FCA's fair value requirements.
              </p>

              <Card className="bg-muted/50">
                <CardContent className="p-6">
                  <p className="text-foreground">
                    This suspension doesn't affect existing policies — if you have GAP cover, it remains 
                    valid. But it does signal a <strong>fundamental acknowledgment that the industry's practices 
                    were problematic</strong>.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Your Next Steps */}
        <section className="py-16 lg:py-20 bg-muted/30">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-6">
                Your Next Steps
              </h2>
              
              <p className="text-lg text-muted-foreground mb-8">
                If you purchased GAP insurance from a car dealer or finance provider, the FCA's 
                findings suggest you may have been affected by industry-wide mis-selling. <strong className="text-foreground">You don't 
                need to wait for a formal redress scheme — you can take action now.</strong>
              </p>

              <Card className="bg-accent/5 border-accent">
                <CardContent className="p-6">
                  <p className="text-foreground">
                    Blue Lion Law can assess your individual circumstances and advise whether you have 
                    grounds for a claim. <strong>Don't wait for a deadline to be announced — by then, it may be 
                    too late.</strong>
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-20 bg-primary">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary-foreground mb-6">
                Don't Wait for a Deadline
              </h2>
              <p className="text-lg text-primary-foreground/80 mb-8">
                Act now while the window is open. Blue Lion Law can assess your claim.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="gold" size="lg" asChild>
                  <a href="https://signup.bluelionclaims.co.uk/" target="_blank" rel="noopener noreferrer">
                    Find Your Agreements
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </a>
                </Button>
                <Button variant="ghost-light" size="lg" className="border border-primary-foreground/30" asChild>
                  <a href="tel:02081530033">
                    Call 0208 153 0033
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="py-12 bg-background border-t border-border">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <h3 className="font-heading text-lg font-bold text-foreground mb-6">Related Articles</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <a href="/gap-insurance/how-to-check-if-gap-was-mis-sold" className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <span className="font-medium text-foreground">How to Check if GAP was Mis-sold</span>
                </a>
                <a href="/gap-insurance/what-is-gap-insurance" className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <span className="font-medium text-foreground">What Is GAP Insurance?</span>
                </a>
                <a href="/gap-insurance-claims" className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <span className="font-medium text-foreground">GAP Insurance Claims</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default FCAActionOnGAPPage;
